import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../core/models/base-state/base-state';
import { PatientReport } from '../../models/patient-report';
import * as actions from '../actions/reports.actions';

export const reportsFeatureKey = 'reports';

export interface ReportsState extends EntityState<PatientReport> {
  baseState: baseState.BaseState;
}

export const reportsAdapter = createEntityAdapter<PatientReport>({
  selectId: report => report.reportId,
});

export const initialState: ReportsState = reportsAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
});

export const reportsReducer = createReducer<ReportsState>(
  initialState,
  on(actions.getReports, (state): ReportsState => {
    return {
      ...reportsAdapter.removeAll(state),
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
    };
  }),

  on(actions.getReports, (state): ReportsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber: 1,
        pageSize: 10,
      },
    };
  }),

  on(actions.getReportsSuccess, (state, { reports }): ReportsState => {
    if (reports !== null) return reportsAdapter.addMany(reports, state);
    return state;
  }),

  on(actions.getReportsSuccess, (state): ReportsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
    };
  }),

  on(actions.getReportsFailure, (state, { error }): ReportsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
    };
  }),

  // Retry of a failed page: clear the error and show loading, keeping pageNumber and every loaded
  // entity. Note it does NOT increment pageNumber the way nextPage below does — retrying with
  // nextPage would skip the page that failed.
  on(
    actions.retryPage,
    (state): ReportsState => ({
      ...state,
      baseState: { ...state.baseState, error: null, isLoading: true },
    })
  ),

  on(actions.nextPage, (state): ReportsState => {
    const currentPage = state.baseState.pageNumber ? state.baseState.pageNumber + 1 : 1;
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber: currentPage,
      },
    };
  })
);
